import { ComponentType } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Component, OnInit, ViewChild, computed } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { cloneDeep, flatten, isEqual, uniq } from 'lodash';
import {
  EMPTY,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  merge,
  startWith,
  switchMap,
} from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { TReducerState } from '../../../../app.config';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableConfiguration,
  TDatatableColumn,
} from '../../../../shared/components/datatable/datatable.interface';
import { IDateRangePickerDate } from '../../../../shared/components/date-range-picker/date-range-picker.interface';
import { InlineDatePickerComponent } from '../../../../shared/components/inline-date-picker/inline-date-picker.component';
import { IDropdownOption } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { DatatablePageConfigurationComponent } from '../../../../shared/components/page-configuration/page-configuration-content/datatable-page-configuration/datatable-page-configuration.component';
import { PageConfigurationComponent } from '../../../../shared/components/page-configuration/page-configuration.component';
import { UserChipComponent } from '../../../../shared/components/user-chip/user-chip.component';
import { userActions } from '../../../../state/user/user.actions';
import { UserEffects } from '../../../../state/user/user.effects';
import { IPageConfigurationValue } from '../../../../state/user/user.interface';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { IItemsFilterCardFields } from '../../items-filter-card/items-filter-card.interface';
import { ApplyChangesRecursivelyQuestionModalComponent } from '../../items-modal/apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from '../../items-modal/apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { RecurringItemService } from '../../items-modal/apply-changes-recursively-question-modal/recurring-item.service';
import { ItemsModalComponent } from '../../items-modal/items-modal.component';
import { BaseItemView } from '../base-item-view.component';
import { ItemViewStore } from '../item-view.store';
import {
  IDatatableItem,
  ILoadCustomFieldsRequest,
  defaultGeneralInformationDatatableColumns,
} from './items-datatable.interface';
import { InlineUserChipComponent } from '../../../../shared/components/inline-user-chip/inline-user-chip.component';
import {
  InlineItemStatusChipComponent
} from '../../../../shared/components/inline-item-status-chip/inline-item-status-chip.component';
import { DueDateTableCellComponent } from './due-date-table-cell/due-date-table-cell.component';
import { ItemStatusChipComponent } from '../../../../shared/components/item-status-chip/item-status-chip.component';

@Component({
  imports: [
    MatCard,
    MatCardContent,
    DatatableComponent,
    UserChipComponent,
    AsyncPipe,
    MatIcon,
    NgIf,
    NgClass,
    InlineDatePickerComponent,
    InlineUserChipComponent,
    InlineItemStatusChipComponent,
    DueDateTableCellComponent,
    ItemStatusChipComponent,
  ],
  providers: [ItemViewStore, HttpUtilitiesService, RecurringItemService],
  selector: 'app-items-datatable',
  styleUrls: ['items-datatable.component.scss'],
  templateUrl: './items-datatable.component.html',
})
export class ItemsDatatableComponent extends BaseItemView implements OnInit {
  @ViewChild(ItemsModalComponent) public itemsModal!: ComponentType<ItemsModalComponent>;

  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(this.formatData);
  });

  public readonly datatableColumns$: Observable<TDatatableColumn<IDatatableItem>[]> = merge(
    PageConfigurationComponent.onApply.pipe(filter(Boolean)),
    PageConfigurationComponent.configurations$.pipe(
      filter((configurations) => Boolean(configurations?.configurationTabs.length)),
      map(DatatablePageConfigurationComponent.initialDataAsApplyMap),
    ),
  ).pipe(
    map((applyData) => {
      const datatableApplyData = applyData.configurations.find((data) => data.type === 'datatable')!;

      return [
        { id: 'id', isSortable: false },
        ...(datatableApplyData.ids
          .map((columnId): TDatatableColumn<IDatatableItem> | null => {
            const defaultColumn = defaultGeneralInformationDatatableColumns.find((column) => column.id === columnId);
            const customColumn = this.store.customFields().find((customField) => customField.id === columnId);

            return !defaultColumn && !customColumn
              ? null
              : {
                  headerClasses: defaultColumn && defaultColumn.headerClasses,
                  id: (defaultColumn?.name as keyof IDatatableItem) ?? customColumn!.name,
                  isSortable: defaultColumn && defaultColumn.isSortable,
                  ...(customColumn
                    ? {
                        dataViewStrategy: this.getCustomColumnDataViewStrategy(customColumn),
                        name: customColumn.name,
                      }
                    : {}),
                };
          })
          .filter(Boolean) as TDatatableColumn<IDatatableItem>[]),
      ];
    }),
  );

  public datatableConfigurations: IDatatableConfiguration<IDatatableItem> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.none,
    dataRefreshEvent: merge(this.setPageToOneAndRefreshDatatable$),
    deleteScope: ETableCrudScope.bulk,
    duplicateScope: ETableCrudScope.none,
    editScope: ETableCrudScope.single,
    specialRows: [
      {
        deleteCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.none;
        },
        duplicateCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.none;
        },
        editCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.single;
        },
        requirement(this: DatatableComponent<{ boardId: number; id: number }>): boolean {
          return this.checkboxModel.selected.some((rowId) => {
            const selectedRow = this.rowData().find((row) => row.id === rowId);

            return (
              selectedRow !== undefined &&
              !UserEffects.getUser().authorizations.adminBoardIds.includes(selectedRow.boardId)
            );
          });
        },
      },
    ],
  };

  private readonly user = this.globalStore.selectSignal((state) => state.user.user);

  constructor(
    public readonly globalStore: Store<TReducerState>,
    private readonly recurringItemService: RecurringItemService,
  ) {
    super('list');
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.getUpdateCustomColumnsSubscription(),
      this.getPageConfigurationUpdateSubscription(),
      this.getSetAsDefaultSubscription(),
    );
  }

  public override initializeDelete(): void {
    const [selectedRow] = this.selectedRows();

    if (!selectedRow) {
      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('system.delete.confirmationSingleContent', {
          context: selectedRow[this.context.nameProperty],
        }),
        submitButtonText: this.translate.instant('button.delete'),
        title: this.translate.instant('system.delete.confirmationTitle', {
          context: this.translate.instant(this.context.singleContext),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (!result) {
              return EMPTY;
            }

            return !selectedRow.itemRecurrenceId
              ? new Observable().pipe(startWith(EItemRecurrenceEditType.onlyThis))
              : this.matDialog
                  .open(ApplyChangesRecursivelyQuestionModalComponent, {
                    data: { mode: ERecursiveAction.delete },
                  })
                  .afterClosed();
          }),
        )
        .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType | undefined) => {
          if (!selectedRecurrenceEditType) {
            return;
          }

          this.store.deleteOneItem({
            id: selectedRow.id,
            ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
              selectedRecurrenceEditType,
              selectedRow.dueDate,
            ),
          });
        }),
    );
  }

  public loadDatatable(): void {
    this.loadItems(this.filterCardData$.value as IItemsFilterCardFields, this.datatableParams$.value!);
  }

  private getSetAsDefaultSubscription(): Subscription {
    return PageConfigurationComponent.onApply
      .pipe(
        filter(
          (applyData) =>
            Boolean(applyData?.isSetAsDefault) &&
            applyData!.configurations.some((configuration) => configuration.type === 'datatable'),
        ),
        withLatestFrom(this.getBoardFilterCardSelection()),
      )
      .subscribe(([applyData, boardFilterCardSelection]) => {
        const { ids } = applyData!.configurations.find((configuration) => configuration.type === 'datatable')!;
        const boardBasedConfigurationName =
          boardFilterCardSelection === null ? 'items' : String(boardFilterCardSelection[0]);
        const componentConfigurations = cloneDeep(this.user()!.pageConfiguration?.ItemsComponent) ?? [];

        if (!componentConfigurations.some((componentConfiguration) => componentConfiguration.name === 'datatable')) {
          componentConfigurations.push({
            name: 'datatable',
            value: [],
          });
        }

        this.globalStore.dispatch(
          userActions.updateUserConfigurations({
            pageConfiguration: {
              ...(this.user()!.pageConfiguration ?? {}),
              ItemsComponent: componentConfigurations.map((componentConfiguration) => {
                return componentConfiguration.name === 'datatable'
                  ? {
                      name: 'datatable',
                      value: [
                        ...componentConfiguration.value.filter(
                          (boardBasedConfiguration) => boardBasedConfiguration.name !== boardBasedConfigurationName,
                        ),
                        { name: boardBasedConfigurationName, value: ids as number[] },
                      ],
                    }
                  : componentConfiguration;
              }),
            },
          }),
        );
      });
  }

  private getUpdateCustomColumnsSubscription(): Subscription {
    return this.filterCardData$
      .pipe(
        filter(Boolean),
        map(
          (filterCard): ILoadCustomFieldsRequest => ({
            isOnlySelf: filterCard.itemScope === 'self',
            parents: filterCard.board?.length ? filterCard.board.map((board) => board.id) : null,
          }),
        ),
        distinctUntilChanged(isEqual),
      )
      .subscribe((requestParams: ILoadCustomFieldsRequest) => {
        PageConfigurationComponent.setPageConfigurationLoading(true);
        this.store.loadCustomFields(requestParams);
      });
  }

  private getCustomColumnDataViewStrategy(customColumn: IDropdownOption): (row: IDatatableItem) => string {
    return (row: IDatatableItem): string => {
      if (!row.values) {
        return '';
      }

      const selectedValue = row.values.find((value) => value.fieldId === customColumn!.id)?.selectedValue;

      if (!selectedValue) {
        return '';
      }

      if (typeof selectedValue === 'string') {
        return this.removeHtmlTags(selectedValue) ?? '';
      }

      if (Array.isArray(selectedValue)) {
        return selectedValue.map((option) => option.name).join(', ');
      }

      return this.getCustomFieldValueFromDayField(selectedValue);
    };
  }

  private getCustomFieldValueFromDayField(selectedValue: IDateRangePickerDate<string>): string {
    if (selectedValue.startDate === null && selectedValue.endDate === null) {
      return '';
    }

    if (
      selectedValue.startDate === null ||
      selectedValue.endDate === null ||
      selectedValue.startDate === selectedValue.endDate
    ) {
      return selectedValue.startDate
        ? DateUtilitiesService.convertUTCToUserFormatted(selectedValue.startDate, true)
        : DateUtilitiesService.convertUTCToUserFormatted(selectedValue.endDate!, true);
    }

    return `${DateUtilitiesService.convertUTCToUserFormatted(selectedValue.startDate, true)} - ${DateUtilitiesService.convertUTCToUserFormatted(selectedValue.endDate, true)}`;
  }

  private getPageConfigurationUpdateSubscription(): Subscription {
    return combineLatest({
      customFields: this.store.customFields$,
      initiallySelectedHeaders: this.getCompiledInitiallySelectedHeaders(),
    })
      .pipe(
        withLatestFrom(
          this.getBoardFilterCardSelection().pipe(
            map((selectedBoardFilters) => !selectedBoardFilters || selectedBoardFilters.length < 2),
          ),
        ),
      )
      .subscribe(([{ customFields, initiallySelectedHeaders }, isSetAsDefaultAvailable]) => {
        PageConfigurationComponent.setPageConfigurationLoading(false);
        PageConfigurationComponent.setPageConfiguration({
          configurationTabs: [
            {
              availableColumnsBySections: [
                {
                  headers: defaultGeneralInformationDatatableColumns.map((row) => ({
                    ...row,
                    name: this.translate.instant(`field.${row.name}`),
                  })),
                  sectionTitle: this.translate.instant('system.dialogSection.generalInformation'),
                },
                {
                  headers: customFields,
                  sectionTitle: this.translate.instant('system.dialogSection.itemFields'),
                },
              ],
              initiallySelectedHeaders,
              lockedHeaders: [-1, -2],
              type: 'datatable',
              unselectedByDefaultHeaders: [],
            },
          ],
          isSetAsDefaultAvailable,
        });
      });
  }

  private getCompiledInitiallySelectedHeaders(): Observable<number[]> {
    return combineLatest({
      apply: merge(
        PageConfigurationComponent.onApply.pipe(
          map((applyData) => applyData?.configurations.find((apply) => apply.type === 'datatable')?.ids ?? null),
        ),
        this.getBoardFilterCardSelection().pipe(map(() => null)),
      ),
      boardFilter: this.getBoardFilterCardSelection(),
      datatableConfiguration: this.globalStore
        .select((state) => state.user.user)
        .pipe(
          map(
            (user): IPageConfigurationValue<string, number[]>[] =>
              (user!.pageConfiguration?.ItemsComponent ?? []).find(
                (configuration) => configuration.name === 'datatable',
              )?.value ?? [],
          ),
        ),
    }).pipe(
      map(({ boardFilter, datatableConfiguration, apply }): number[] => {
        if (apply) {
          return apply as number[];
        }

        const relatedSetAsDefaults = datatableConfiguration.filter(
          (boardConfiguration) =>
            (boardFilter === null && boardConfiguration.name === 'items') ||
            (boardFilter !== null && boardFilter.includes(Number(boardConfiguration.name))),
        );

        return uniq(flatten(relatedSetAsDefaults.map((setAsDefault) => setAsDefault.value)));
      }),
    );
  }

  private getBoardFilterCardSelection(): Observable<number[] | null> {
    return this.filterCardData$.pipe(
      filter(Boolean),
      map((filterCard): number[] | null =>
        filterCard.board?.length ? filterCard.board.map((board) => board.id) : null,
      ),
      distinctUntilChanged(isEqual),
    );
  }

  private removeHtmlTags(html: string): string | null {
    const doc: Document = new DOMParser().parseFromString(html, 'text/html');

    return doc.body.textContent;
  }
}
